import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import useBranch from '../../hooks/useBranch';
import useRegiondoValueVoucherImport from '../../hooks/useRegiondoValueVoucherImport';

const headerAliases = {
  voucherCode: ['gutscheincode', 'voucher code', 'code'],
  type: ['typ', 'type'],
  buyer: ['kaeufer', 'käufer', 'buyer'],
  product: ['produkt', 'product'],
  value: ['preis/ leistung', 'preis/leistung', 'wert', 'value'],
  remaining: ['verbleibend', 'remaining', 'restwert'],
  issuedAt: ['ausgestellt', 'issued', 'issued at'],
  expiresAt: ['ablauf', 'expires', 'expires at'],
  status: ['status'],
};

const emailRegex = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;

function normalizeHeader(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\u00a0/g, ' ');
}

function parseCsvRecords(text) {
  const source = String(text || '').replace(/^\uFEFF/, '');
  const records = [];
  let row = [];
  let cell = '';
  let inQuotes = false;

  for (let i = 0; i < source.length; i++) {
    const char = source[i];
    const next = source[i + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        cell += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (!inQuotes && char === ';') {
      row.push(cell);
      cell = '';
      continue;
    }

    if (!inQuotes && (char === '\n' || char === '\r')) {
      if (char === '\r' && next === '\n') i++;
      row.push(cell);
      if (row.some(value => String(value || '').trim())) records.push(row);
      row = [];
      cell = '';
      continue;
    }

    cell += char;
  }

  row.push(cell);
  if (row.some(value => String(value || '').trim())) records.push(row);
  return records;
}

function buildHeaderIndex(headers) {
  const normalized = headers.map(normalizeHeader);
  const index = {};

  for (const [field, aliases] of Object.entries(headerAliases)) {
    index[field] = normalized.findIndex(header => aliases.includes(header));
  }

  return index;
}

function extractMailAndName(buyer) {
  const raw = String(buyer || '').replace(/\s+/g, ' ').trim();
  const mail = raw.match(emailRegex)?.[0] || '';
  const name = raw.replace(emailRegex, '').replace(/\s+/g, ' ').trim();
  return { buyerMail: mail, buyerName: name };
}

function parseRegiondoCsv(text) {
  const records = parseCsvRecords(text);
  if (records.length < 2) {
    throw new Error('CSV enthaelt keine Gutscheinzeilen.');
  }

  const headers = records[0];
  const headerIndex = buildHeaderIndex(headers);
  const requiredFields = ['voucherCode', 'value', 'remaining', 'expiresAt'];
  const missing = requiredFields.filter(field => headerIndex[field] < 0);
  if (missing.length > 0) {
    throw new Error(`CSV-Spalten fehlen: ${missing.join(', ')}`);
  }

  return records.slice(1).map((record, index) => {
    const get = field => {
      const col = headerIndex[field];
      return col >= 0 ? String(record[col] ?? '').trim() : '';
    };
    const buyer = get('buyer');
    const { buyerMail, buyerName } = extractMailAndName(buyer);

    return {
      rowNumber: index + 2,
      voucherCode: get('voucherCode'),
      type: get('type'),
      buyer,
      buyerName,
      buyerMail,
      product: get('product'),
      value: get('value'),
      remaining: get('remaining'),
      issuedAt: get('issuedAt'),
      expiresAt: get('expiresAt'),
      status: get('status'),
    };
  });
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsText(file, 'utf-8');
  });
}

function formatCurrency(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return '-';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n);
}

function StatCard({ title, value, subtitle }) {
  return (
    <Card variant='outlined' sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant='overline' color='text.secondary'>
          {title}
        </Typography>
        <Typography variant='h4' sx={{ fontWeight: 700 }}>
          {value}
        </Typography>
        {subtitle ? (
          <Typography variant='body2' color='text.secondary'>
            {subtitle}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
}

export default function RegiondoValueVoucherImport() {
  const fileInputRef = useRef(null);
  const { getBranches } = useBranch();
  const { isLoading, previewImport, executeImport } = useRegiondoValueVoucherImport();

  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [fileName, setFileName] = useState('');
  const [parsedRows, setParsedRows] = useState([]);
  const [analysis, setAnalysis] = useState(null);

  useEffect(() => {
    let active = true;
    const loadBranches = async () => {
      try {
        const response = await getBranches();
        if (!active) return;
        setBranches(Array.isArray(response) ? response : []);
      } catch (err) {
        toast.error('Fehler beim Laden der Standorte');
      }
    };

    loadBranches();
    return () => {
      active = false;
    };
  }, [getBranches]);

  const selectedBranch = useMemo(
    () => branches.find(branch => String(branch._id) === String(selectedBranchId)),
    [branches, selectedBranchId]
  );

  const previewRows = analysis?.rows || [];
  const importableRows = previewRows.filter(row => row.importable);
  const invalidRows = previewRows.filter(row => !row.importable);

  const handleFileChange = useCallback(async event => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await readFileAsText(file);
      const rows = parseRegiondoCsv(text);
      setFileName(file.name);
      setParsedRows(rows);
      setAnalysis(null);
      toast.success(`${rows.length} CSV-Zeilen gelesen`);
    } catch (err) {
      setFileName('');
      setParsedRows([]);
      setAnalysis(null);
      toast.error(err.message || 'CSV konnte nicht gelesen werden');
    }
  }, []);

  const runPreview = useCallback(async () => {
    if (!selectedBranchId) {
      toast.info('Bitte Standort auswaehlen');
      return;
    }
    if (parsedRows.length === 0) {
      toast.info('Bitte CSV-Datei hochladen');
      return;
    }

    const result = await previewImport({ branchId: selectedBranchId, vouchers: parsedRows });
    setAnalysis(result);
    if (result?.summary?.invalidRows > 0) {
      toast.info(`${result.summary.invalidRows} Zeilen brauchen Aufmerksamkeit`);
    } else {
      toast.success('Pruefung erfolgreich');
    }
  }, [parsedRows, previewImport, selectedBranchId]);

  const runImport = useCallback(async () => {
    if (!analysis || importableRows.length === 0) {
      toast.info('Bitte zuerst eine Pruefung mit importierbaren Zeilen durchfuehren');
      return;
    }

    const result = await executeImport({ branchId: selectedBranchId, vouchers: parsedRows });
    const skipped = Number(result?.skippedCount || 0);
    toast.success(
      `${result?.importedCount || 0} Regiondo-Wertgutscheine importiert${skipped ? `, ${skipped} uebersprungen` : ''}`
    );
    setAnalysis(null);
    setParsedRows([]);
    setFileName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [analysis, executeImport, importableRows.length, parsedRows, selectedBranchId]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant='h4' sx={{ fontWeight: 700 }}>
            Regiondo-Wertgutschein-Import
          </Typography>
          <Typography color='text.secondary'>
            Importiert aktive Regiondo-Wertgutscheine als normale Wertgutscheine ohne PIN. Der exakte
            Gutscheincode bleibt erhalten und die Einloesung wird auf den gewaehlten Standort begrenzt.
          </Typography>
        </Box>

        <Card variant='outlined'>
          <CardContent>
            <Grid container spacing={2} alignItems='center'>
              <Grid item xs={12} md={5}>
                <FormControl fullWidth>
                  <InputLabel id='regiondo-value-voucher-branch-label'>Standort</InputLabel>
                  <Select
                    labelId='regiondo-value-voucher-branch-label'
                    label='Standort'
                    value={selectedBranchId}
                    onChange={event => setSelectedBranchId(event.target.value)}
                  >
                    {branches.map(branch => (
                      <MenuItem key={branch._id} value={branch._id}>
                        {branch.longName || branch.locationName || branch.name || branch._id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button variant='outlined' component='label' fullWidth>
                  CSV auswaehlen
                  <input
                    ref={fileInputRef}
                    hidden
                    type='file'
                    accept='.csv,text/csv'
                    onChange={handleFileChange}
                  />
                </Button>
                {fileName ? (
                  <Typography variant='caption' color='text.secondary'>
                    {fileName}
                  </Typography>
                ) : null}
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant='contained'
                  fullWidth
                  onClick={runPreview}
                  disabled={isLoading || !selectedBranchId || parsedRows.length === 0}
                >
                  {isLoading ? <CircularProgress size={20} /> : 'Pruefen'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {parsedRows.length > 0 ? (
          <Alert severity='info'>
            {parsedRows.length} Zeilen aus der CSV geladen
            {selectedBranch ? `, Zielstandort: ${selectedBranch.longName || selectedBranch.locationName}` : ''}.
          </Alert>
        ) : null}

        {analysis ? (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <StatCard title='Importierbar' value={analysis.summary.importableRows} subtitle='Zeilen' />
              </Grid>
              <Grid item xs={12} md={3}>
                <StatCard title='Fehlerhaft' value={analysis.summary.invalidRows} subtitle='Zeilen' />
              </Grid>
              <Grid item xs={12} md={3}>
                <StatCard title='Bereits vorhanden' value={analysis.summary.existingCodes} subtitle='Codes' />
              </Grid>
              <Grid item xs={12} md={3}>
                <StatCard
                  title='Restwert'
                  value={formatCurrency(analysis.summary.totalRemainingValue)}
                  subtitle='importierbar'
                />
              </Grid>
            </Grid>

            <Card variant='outlined'>
              <CardContent>
                <Stack direction='row' justifyContent='space-between' alignItems='center' gap={2}>
                  <Box>
                    <Typography variant='h6'>Vorschau</Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {importableRows.length} importierbar, {invalidRows.length} mit Fehlern.
                    </Typography>
                  </Box>
                  <Button
                    variant='contained'
                    color='success'
                    onClick={runImport}
                    disabled={isLoading || importableRows.length === 0}
                  >
                    {isLoading ? <CircularProgress size={20} /> : 'Jetzt importieren'}
                  </Button>
                </Stack>
                <Divider sx={{ my: 2 }} />
                <Paper variant='outlined'>
                  <Box sx={{ maxHeight: 520, overflow: 'auto' }}>
                    <Table size='small' stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Status</TableCell>
                          <TableCell>Zeile</TableCell>
                          <TableCell>Code</TableCell>
                          <TableCell>Kaeufer</TableCell>
                          <TableCell>Mail</TableCell>
                          <TableCell>Produkt</TableCell>
                          <TableCell align='right'>Wert</TableCell>
                          <TableCell align='right'>Rest</TableCell>
                          <TableCell>Ablauf</TableCell>
                          <TableCell>Hinweise</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewRows.slice(0, 1000).map(row => (
                          <TableRow key={`${row.rowNumber}-${row.voucherCode}`} hover>
                            <TableCell>
                              <Chip
                                size='small'
                                color={row.importable ? 'success' : 'error'}
                                label={row.importable ? 'OK' : 'Fehler'}
                              />
                            </TableCell>
                            <TableCell>{row.rowNumber}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{row.voucherCode}</TableCell>
                            <TableCell>{row.buyerName || '-'}</TableCell>
                            <TableCell>{row.buyerMail || '-'}</TableCell>
                            <TableCell>{row.product || '-'}</TableCell>
                            <TableCell align='right'>{formatCurrency(row.value)}</TableCell>
                            <TableCell align='right'>{formatCurrency(row.remaining)}</TableCell>
                            <TableCell>
                              {row.expiresAt ? new Date(row.expiresAt).toLocaleDateString('de-DE') : '-'}
                            </TableCell>
                            <TableCell>{row.errors?.length ? row.errors.join(' ') : '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </Paper>
              </CardContent>
            </Card>
          </>
        ) : null}
      </Stack>
    </Box>
  );
}
